import {
	type BoxSize,
	beginPanGesture,
	type ClientPoint,
	clampPanOffset,
	IDLE_PAN_GESTURE,
	keyboardPanOffset,
	movePanGesture,
	type PanGesture,
	type PanOffset,
	zoomedPictureOwnsTouch,
} from '@polygraf/shared'
import {
	type KeyboardEvent as ReactKeyboardEvent,
	type PointerEvent as ReactPointerEvent,
	type TouchEvent as ReactTouchEvent,
	type RefObject,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react'

interface UseZoomPanOptions {
	/** The clipping pane; the zoomed picture is a pane-sized box inside it. */
	paneRef: RefObject<HTMLElement | null>
	zoom: number
	zoomLocked: boolean
	minZoom: number
	maxZoom: number
	/** A two-finger spread changed the zoom. */
	onZoomChange: (zoom: number) => void
	/**
	 * The picture as drawn at 100 % inside a pane of this size, for pictures
	 * that do not fill their pane (a letterboxed `object-contain` video).
	 * Omitted: the picture fills the pane.
	 */
	contentSize?: (pane: BoxSize) => BoxSize
}

function touchPoints(e: ReactTouchEvent): ClientPoint[] {
	return Array.from(e.touches, (t) => ({ x: t.clientX, y: t.clientY }))
}

/**
 * Pan (and pinch-zoom) a zoomed picture inside its pane: one finger or the
 * mouse drags it 1:1, two fingers pan by their midpoint and zoom by their
 * spread, arrow keys move it a tenth of the pane. The rules are the pure
 * beginPanGesture / movePanGesture / keyboardPanOffset in @polygraf/shared;
 * this hook only feeds them events.
 *
 * Spread `handlers` on the pane, give it `touchAction` (the browser must not
 * scroll or zoom the page under a finger that pans the zoomed picture, but it
 * keeps both at or below 100 %; see zoomedPictureOwnsTouch), and apply
 * `transform` to the pane-sized wrapper of the picture.
 *
 * No handler stops propagation or prevents a click, so a double tap still
 * reaches useImmersiveDoubleTap on an ancestor, which already refuses to
 * count a press that travelled or used two fingers as a tap.
 */
export function useZoomPan({
	paneRef,
	zoom,
	zoomLocked,
	minZoom,
	maxZoom,
	onZoomChange,
	contentSize,
}: UseZoomPanOptions) {
	const [pan, setPan] = useState<PanOffset>({ x: 0, y: 0 })
	const gestureRef = useRef<PanGesture>(IDLE_PAN_GESTURE)
	// Gestures start from the view as it is NOW, not as it was when the
	// handlers were last created.
	const viewRef = useRef({ zoom, pan })
	viewRef.current = { zoom, pan }
	const limitsRef = useRef({ minZoom, maxZoom, zoomLocked })
	limitsRef.current = { minZoom, maxZoom, zoomLocked }
	const onZoomChangeRef = useRef(onZoomChange)
	onZoomChangeRef.current = onZoomChange
	const contentSizeRef = useRef(contentSize)
	contentSizeRef.current = contentSize
	const contentOf = useCallback((pane: BoxSize) => contentSizeRef.current?.(pane) ?? pane, [])

	const restart = useCallback(
		(points: readonly ClientPoint[]) => {
			const rect = paneRef.current?.getBoundingClientRect()
			gestureRef.current = rect ? beginPanGesture(points, viewRef.current, rect) : IDLE_PAN_GESTURE
		},
		[paneRef],
	)

	const move = useCallback(
		(points: readonly ClientPoint[]) => {
			const rect = paneRef.current?.getBoundingClientRect()
			if (!rect) return
			const next = movePanGesture(gestureRef.current, points, {
				pane: rect,
				content: contentOf(rect),
				zoom: viewRef.current.zoom,
				limits: limitsRef.current,
			})
			if (!next) return
			setPan((prev) => (prev.x === next.pan.x && prev.y === next.pan.y ? prev : next.pan))
			if (next.zoom !== viewRef.current.zoom) onZoomChangeRef.current(next.zoom)
		},
		[paneRef, contentOf],
	)

	// Zooming out (buttons, wheel, pinch), a smaller pane (rotation, the
	// control bar, a split layout) or a picture of another shape (the
	// presenter switched from a 16:9 screen to a 4:3 camera) can leave an
	// offset that pulls the picture off the opposite edge, into a letterbox
	// bar, or off-centre at 100 %. Re-clamp on all three. A video announces a
	// new size with `loadedmetadata` / `resize`, which do not bubble, so they
	// are caught on the way down (capture) at the pane.
	useEffect(() => {
		const el = paneRef.current
		if (!el) return
		const reclamp = () => {
			const rect = el.getBoundingClientRect()
			setPan((prev) => {
				const next = clampPanOffset(prev, zoom, rect, contentOf(rect))
				return next.x === prev.x && next.y === prev.y ? prev : next
			})
		}
		reclamp()
		const observer = new ResizeObserver(reclamp)
		observer.observe(el)
		el.addEventListener('loadedmetadata', reclamp, true)
		el.addEventListener('resize', reclamp, true)
		return () => {
			observer.disconnect()
			el.removeEventListener('loadedmetadata', reclamp, true)
			el.removeEventListener('resize', reclamp, true)
		}
	}, [paneRef, zoom, contentOf])

	// Touch: every change in the number of fingers restarts the gesture from
	// the fingers still down, so lifting one finger of a pinch carries on as a
	// drag instead of jumping. `touchcancel` (a call, a system edge swipe)
	// arrives instead of `touchend` and must end the gesture the same way.
	const onTouchStart = useCallback((e: ReactTouchEvent) => restart(touchPoints(e)), [restart])
	const onTouchMove = useCallback((e: ReactTouchEvent) => move(touchPoints(e)), [move])
	const onTouchEnd = useCallback((e: ReactTouchEvent) => restart(touchPoints(e)), [restart])

	// Mouse drag. Pointer events filtered to the mouse, because touch already
	// has its own path above and iOS follows a tap with compatibility mouse
	// events that must not start a second drag.
	const onPointerDown = useCallback(
		(e: ReactPointerEvent) => {
			if (e.pointerType !== 'mouse' || e.button !== 0) return
			restart([{ x: e.clientX, y: e.clientY }])
		},
		[restart],
	)
	const onPointerMove = useCallback(
		(e: ReactPointerEvent) => {
			if (e.pointerType !== 'mouse') return
			// The button was released outside the pane.
			if ((e.buttons & 1) === 0) {
				gestureRef.current = IDLE_PAN_GESTURE
				return
			}
			move([{ x: e.clientX, y: e.clientY }])
		},
		[move],
	)
	const onPointerUp = useCallback((e: ReactPointerEvent) => {
		if (e.pointerType === 'mouse') gestureRef.current = IDLE_PAN_GESTURE
	}, [])

	const onKeyDown = useCallback(
		(e: ReactKeyboardEvent) => {
			if (e.altKey || e.ctrlKey || e.metaKey) return
			const rect = paneRef.current?.getBoundingClientRect()
			if (!rect) return
			const next = keyboardPanOffset(e.key, viewRef.current, rect, contentOf(rect))
			if (!next) return
			// The arrow is ours: it must not also scroll the page.
			e.preventDefault()
			setPan(next)
		},
		[paneRef, contentOf],
	)

	return {
		pan,
		/**
		 * `translate` before `scale`: transform functions apply right to left,
		 * so the offset is in the pane's own pixels, not multiplied by the zoom,
		 * and the picture travels exactly with the finger.
		 */
		transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
		/**
		 * For the pane. `none` while the zoomed picture owns the fingers.
		 * Otherwise `manipulation`: the browser scrolls and pinch-zooms the
		 * page as usual, and only its double-tap-to-zoom is off, so a double
		 * tap reaches the immersive toggle without a zoom or a click delay.
		 */
		touchAction: zoomedPictureOwnsTouch(zoom) ? ('none' as const) : ('manipulation' as const),
		handlers: {
			onTouchStart,
			onTouchMove,
			onTouchEnd,
			onTouchCancel: onTouchEnd,
			onPointerDown,
			onPointerMove,
			onPointerUp,
			onPointerLeave: onPointerUp,
			onKeyDown,
		},
	}
}
